import { useState, useEffect, useCallback, useMemo } from 'react'
import apiService from 'services/api'
import { ContactCompanySearchResult } from 'models/companies'
import { transformFirstBiteResults } from '../utils/searchUtils'
import { Filters, MIN_SEARCH_CHARS } from '../constants'

interface SearchResults {
  firstBiteResults: ContactCompanySearchResult[]
  unmatchedResults: ContactCompanySearchResult[]
  firstBiteDoorResults: any[]
  unmatchedDoorResults: any[]
  firstBiteCount: number
  unmatchedCount: number
  firstBiteDoorCount: number
  unmatchedDoorCount: number
  firstBiteTotalRows: number
  unmatchedTotalRows: number
  firstBiteDoorTotalRows: number
  unmatchedDoorTotalRows: number
  isLoading: boolean
  currentResults: ContactCompanySearchResult[] | any[]
  currentTotalRows: number
  searchType: 'address' | 'domain' | 'name' | null
}

interface UseUnifiedSearchAPIProps {
  searchTerm: string
  filters: Filters
  sortBy: string
  firstBitePage: number
  unmatchedPage: number
  firstBiteDoorPage: number
  unmatchedDoorPage: number
  selectedTab: 'firstbite' | 'unmatched' | 'firstbitedoors' | 'unmatcheddoors'
  pageSize: number
}

export function useUnifiedSearchAPI({
  searchTerm,
  filters,
  sortBy,
  firstBitePage,
  unmatchedPage,
  firstBiteDoorPage,
  unmatchedDoorPage,
  selectedTab,
  pageSize,
}: UseUnifiedSearchAPIProps): SearchResults {
  const api = useMemo(() => apiService(), [])

  // State for search results
  const [isLoading, setIsLoading] = useState(false)
  const [firstBiteResults, setFirstBiteResults] = useState<
    ContactCompanySearchResult[]
  >([])
  const [unmatchedResults, setUnmatchedResults] = useState<
    ContactCompanySearchResult[]
  >([])
  const [firstBiteDoorResults, setFirstBiteDoorResults] = useState<any[]>([])
  const [unmatchedDoorResults, setUnmatchedDoorResults] = useState<any[]>([])
  const [firstBiteCount, setFirstBiteCount] = useState(0)
  const [unmatchedCount, setUnmatchedCount] = useState(0)
  const [firstBiteDoorCount, setFirstBiteDoorCount] = useState(0)
  const [unmatchedDoorCount, setUnmatchedDoorCount] = useState(0)
  const [firstBiteTotalRows, setFirstBiteTotalRows] = useState(0)
  const [unmatchedTotalRows, setUnmatchedTotalRows] = useState(0)
  const [firstBiteDoorTotalRows, setFirstBiteDoorTotalRows] = useState(0)
  const [unmatchedDoorTotalRows, setUnmatchedDoorTotalRows] = useState(0)
  const [searchType, setSearchType] = useState<
    'address' | 'domain' | 'name' | null
  >(null)

  // Build search options for both tabs
  const firstBiteSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (firstBitePage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, firstBitePage])

  const unmatchedSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (unmatchedPage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, unmatchedPage])

  const firstBiteDoorSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (firstBiteDoorPage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, firstBiteDoorPage])

  const unmatchedDoorSearchOptions = useMemo(() => {
    return {
      category_filter: filters.category === 'all' ? 'all' : filters.category,
      type_filter: filters.type === 'all' ? 'all' : filters.type,
      sort_by: sortBy,
      limit: pageSize,
      offset: (unmatchedDoorPage - 1) * pageSize,
    }
  }, [filters.category, filters.type, sortBy, pageSize, unmatchedDoorPage])

  // Single search function that handles all tabs
  const performSearch = useCallback(
    async (
      searchValue: string,
      fbOptions: any,
      umOptions: any,
      fbDoorOptions: any,
      umDoorOptions: any
    ) => {
      if (!searchValue.trim()) {
        setFirstBiteResults([])
        setUnmatchedResults([])
        setFirstBiteDoorResults([])
        setUnmatchedDoorResults([])
        setFirstBiteCount(0)
        setUnmatchedCount(0)
        setFirstBiteDoorCount(0)
        setUnmatchedDoorCount(0)
        setFirstBiteTotalRows(0)
        setUnmatchedTotalRows(0)
        setFirstBiteDoorTotalRows(0)
        setUnmatchedDoorTotalRows(0)
        setSearchType(null)
        return
      }

      try {
        setIsLoading(true)
        // Clear the search type when starting a new search
        setSearchType(null)

        // Perform all searches in parallel with their respective pagination
        const [
          firstBiteData,
          unmatchedData,
          firstBiteDoorData,
          unmatchedDoorData,
        ] = await Promise.all([
          api.searchFirstBiteCompanies(searchValue, fbOptions),
          api.searchUnmatchedCompanies(searchValue, umOptions),
          api.searchFirstBiteDoors(searchValue, fbDoorOptions),
          api.searchUnmatchedDoors(searchValue, umDoorOptions),
        ])

        // Handle response format
        const firstBiteResults = firstBiteData.results || []
        const unmatchedResults = unmatchedData.results || []
        const firstBiteDoorResults = firstBiteDoorData.results || []
        const unmatchedDoorResults = unmatchedDoorData.results || []
        const firstBiteTotal = firstBiteData.total || 0
        const unmatchedTotal = unmatchedData.total || 0
        const firstBiteDoorTotal = firstBiteDoorData.total || 0
        const unmatchedDoorTotal = unmatchedDoorData.total || 0

        // Get search type from any of the responses (they should all be the same)
        const detectedSearchType =
          firstBiteData.search_type ||
          unmatchedData.search_type ||
          firstBiteDoorData.search_type ||
          unmatchedDoorData.search_type ||
          'name'

        // Transform First Bite results
        const transformedFirstBite = transformFirstBiteResults(firstBiteResults)

        setFirstBiteResults(transformedFirstBite)
        setUnmatchedResults(unmatchedResults)
        setFirstBiteDoorResults(firstBiteDoorResults)
        setUnmatchedDoorResults(unmatchedDoorResults)
        setFirstBiteCount(firstBiteTotal)
        setUnmatchedCount(unmatchedTotal)
        setFirstBiteDoorCount(firstBiteDoorTotal)
        setUnmatchedDoorCount(unmatchedDoorTotal)
        setFirstBiteTotalRows(firstBiteTotal)
        setUnmatchedTotalRows(unmatchedTotal)
        setFirstBiteDoorTotalRows(firstBiteDoorTotal)
        setUnmatchedDoorTotalRows(unmatchedDoorTotal)
        setSearchType(detectedSearchType as 'address' | 'domain' | 'name')
      } catch (error) {
        // Reset on error
        setFirstBiteResults([])
        setUnmatchedResults([])
        setFirstBiteDoorResults([])
        setUnmatchedDoorResults([])
        setFirstBiteCount(0)
        setUnmatchedCount(0)
        setFirstBiteDoorCount(0)
        setUnmatchedDoorCount(0)
        setFirstBiteTotalRows(0)
        setUnmatchedTotalRows(0)
        setFirstBiteDoorTotalRows(0)
        setUnmatchedDoorTotalRows(0)
        setSearchType(null)
      } finally {
        setIsLoading(false)
      }
    },
    [api]
  )

  // Trigger search when search term or options change
  useEffect(() => {
    if (searchTerm.length >= MIN_SEARCH_CHARS) {
      performSearch(
        searchTerm,
        firstBiteSearchOptions,
        unmatchedSearchOptions,
        firstBiteDoorSearchOptions,
        unmatchedDoorSearchOptions
      )
    } else {
      setFirstBiteResults([])
      setUnmatchedResults([])
      setFirstBiteDoorResults([])
      setUnmatchedDoorResults([])
      setFirstBiteCount(0)
      setUnmatchedCount(0)
      setFirstBiteDoorCount(0)
      setUnmatchedDoorCount(0)
      setFirstBiteTotalRows(0)
      setUnmatchedTotalRows(0)
      setFirstBiteDoorTotalRows(0)
      setUnmatchedDoorTotalRows(0)
    }
  }, [
    searchTerm,
    firstBiteSearchOptions,
    unmatchedSearchOptions,
    firstBiteDoorSearchOptions,
    unmatchedDoorSearchOptions,
    performSearch,
  ])

  // Handle client-side sorting for non-relevance sorts
  useEffect(() => {
    if (sortBy === 'relevance') return

    const sortResults = (results: ContactCompanySearchResult[]) => {
      return [...results].sort((a, b) => {
        switch (sortBy) {
          case 'alphabetical_asc':
          case 'alphabetical':
            return a.name.localeCompare(b.name)
          case 'alphabetical_desc':
            return b.name.localeCompare(a.name)
          case 'size_desc':
          case 'size':
            return (b.size || 0) - (a.size || 0)
          case 'size_asc':
            return (a.size || 0) - (b.size || 0)
          default:
            return 0
        }
      })
    }

    if (firstBiteResults.length > 0) {
      setFirstBiteResults(sortResults(firstBiteResults))
    }
    if (unmatchedResults.length > 0) {
      setUnmatchedResults(sortResults(unmatchedResults))
    }
    if (firstBiteDoorResults.length > 0) {
      setFirstBiteDoorResults(sortResults(firstBiteDoorResults))
    }
    if (unmatchedDoorResults.length > 0) {
      setUnmatchedDoorResults(sortResults(unmatchedDoorResults))
    }
  }, [
    sortBy,
    firstBiteResults.length,
    unmatchedResults.length,
    firstBiteDoorResults.length,
    unmatchedDoorResults.length,
  ])

  // Get current results based on selected tab
  const currentResults =
    selectedTab === 'firstbite'
      ? firstBiteResults
      : selectedTab === 'firstbitedoors'
        ? firstBiteDoorResults
        : selectedTab === 'unmatcheddoors'
          ? unmatchedDoorResults
          : unmatchedResults
  const currentTotalRows =
    selectedTab === 'firstbite'
      ? firstBiteTotalRows
      : selectedTab === 'firstbitedoors'
        ? firstBiteDoorTotalRows
        : selectedTab === 'unmatcheddoors'
          ? unmatchedDoorTotalRows
          : unmatchedTotalRows

  return {
    firstBiteResults,
    unmatchedResults,
    firstBiteDoorResults,
    unmatchedDoorResults,
    firstBiteCount,
    unmatchedCount,
    firstBiteDoorCount,
    unmatchedDoorCount,
    firstBiteTotalRows,
    unmatchedTotalRows,
    firstBiteDoorTotalRows,
    unmatchedDoorTotalRows,
    isLoading,
    currentResults,
    currentTotalRows,
    searchType,
  }
}
